import { useState, useEffect, useCallback } from 'react';
import type { Strategy, AssignedAccount } from '@/types/strategy';

// ─── Types ────────────────────────────────────────────────────────

interface CtraderAccount {
  id: number;
  account_id: string;
  account_name: string;
  balance: number;
  currency: string;
  is_live: boolean;
}

interface Mt4Assignment {
  mt4AccountId: string;
  mt4AccountName: string;
  ctraderAccountId: string;
  strategyId: string;
}

// ─── Helpers ──────────────────────────────────────────────────────

function cn(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ');
}

// ─── Component ────────────────────────────────────────────────────

interface CopyTargetsProps {
  strategies: Strategy[];
  selectedAccountId: string;
  ctraderAccounts: CtraderAccount[];
  userConnectionMt4s?: Array<{ account_id: string; account_name: string; is_live: boolean }>;
}

const STORAGE_KEY = 'mt4_copy_assignments';

export default function CopyTargets({ strategies, selectedAccountId, ctraderAccounts, userConnectionMt4s = [] }: CopyTargetsProps) {
  // Load saved assignments from localStorage
  const loadAssignments = useCallback((): Mt4Assignment[] => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }, []);

  const [assignments, setAssignments] = useState<Mt4Assignment[]>(loadAssignments);
  const [hasChanges, setHasChanges] = useState(false);

  // ─── Collect all unique MT4 accounts (from user_connections first, then strategies) ─────────

  const mt4Accounts: AssignedAccount[] = [];
  const seenMt4 = new Set<string>();

  // From user_connections (Dashboard MT4 Master Accounts) — appear first
  for (const uc of userConnectionMt4s) {
    const key = uc.account_id;
    if (!seenMt4.has(key)) {
      seenMt4.add(key);
      mt4Accounts.push({
        id: `uc-${uc.account_id}`,
        platform: 'mt4',
        account_id: uc.account_id,
        account_name: uc.account_name,
        is_live: uc.is_live,
      });
    }
  }

  // From strategies assigned_accounts
  for (const s of strategies) {
    for (const assigned of s.assigned_accounts) {
      if (assigned.platform !== 'mt4') continue;
      const key = assigned.account_id;
      if (!seenMt4.has(key)) {
        seenMt4.add(key);
        mt4Accounts.push(assigned);
      }
    }
  }

  // ─── Get current assignment for an MT4 account ────────────────

  const getAssignment = (mt4AccountId: string): Mt4Assignment | undefined => {
    return assignments.find((a) => a.mt4AccountId === mt4AccountId);
  };

  // ─── Update assignment ────────────────────────────────────────

  const updateAssignment = (mt4AccountId: string, mt4AccountName: string, field: 'ctraderAccountId' | 'strategyId', value: string) => {
    setAssignments((prev) => {
      const existing = prev.find((a) => a.mt4AccountId === mt4AccountId);
      if (existing) {
        return prev.map((a) =>
          a.mt4AccountId === mt4AccountId ? { ...a, [field]: value } : a
        );
      }
      return [
        ...prev,
        {
          mt4AccountId,
          mt4AccountName,
          ctraderAccountId: field === 'ctraderAccountId' ? value : '',
          strategyId: field === 'strategyId' ? value : '',
        },
      ];
    });
    setHasChanges(true);
  };

  // ─── Get strategy name by ID ─────────────────────────────────

  const getStrategyName = (id: string): string => {
    const s = strategies.find((st) => st.id === id);
    return s ? s.name : 'Unknown';
  };

  const getAccountName = (id: string): string => {
    const acc = ctraderAccounts.find((a) => a.account_id === id || String(a.id) === id);
    return acc ? acc.account_name : id;
  };

  // ─── Filter strategies by selected cTrader source account ────

  const getStrategiesForCtraderSource = (ctraderSourceId: string): Strategy[] => {
    if (!ctraderSourceId) return strategies;
    return strategies.filter((s) =>
      s.assigned_accounts.some(
        (aa) =>
          aa.platform === 'ctrader' &&
          (aa.account_id === ctraderSourceId || aa.account_id === String(ctraderSourceId))
      )
    );
  };

  // ─── Save to localStorage ────────────────────────────────────

  const saveAssignments = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(assignments));
    setHasChanges(false);
  };

  // ─── Sync from localStorage on mount ─────────────────────────

  useEffect(() => {
    setAssignments(loadAssignments());
  }, [loadAssignments]);

  // ─── Render ──────────────────────────────────────────────────

  if (mt4Accounts.length === 0) {
    return (
      <div className="bg-background-100 rounded-xl border border-background-200/70 p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg bg-accent-500/10 flex items-center justify-center">
            <i className="ri-link text-accent-400 text-sm"></i>
          </div>
          <h4 className="text-xs font-heading font-semibold text-foreground-900">MT4 Copy Targets</h4>
        </div>
        <div className="text-center py-4">
          <div className="w-8 h-8 rounded-full bg-background-200/70 flex items-center justify-center mx-auto mb-2">
            <i className="ri-link-unlink text-foreground-400 text-sm"></i>
          </div>
          <p className="text-xs text-foreground-500">No MT4 accounts configured</p>
          <p className="text-[10px] text-foreground-400 mt-0.5">Assign MT4 accounts to strategies to see them here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background-100 rounded-xl border border-background-200/70 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-background-200/70 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-accent-500/10 flex items-center justify-center">
            <i className="ri-link text-accent-400 text-sm"></i>
          </div>
          <div>
            <h4 className="text-xs font-heading font-semibold text-foreground-900">MT4 Copy Targets</h4>
            <p className="text-[10px] text-foreground-500">{mt4Accounts.length} account{mt4Accounts.length > 1 ? 's' : ''}</p>
          </div>
        </div>
        {hasChanges && (
          <button
            type="button"
            onClick={saveAssignments}
            className="flex items-center gap-1 px-2.5 py-1.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-md text-[10px] font-medium transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-save-line"></i>
            Save
          </button>
        )}
      </div>

      {/* MT4 Account Cards */}
      <div className="p-3 space-y-2">
        {mt4Accounts.map((mt4) => {
          const assignment = getAssignment(mt4.account_id);
          const sourceStrategies = getStrategiesForCtraderSource(assignment?.ctraderAccountId || '');

          return (
            <div
              key={mt4.account_id}
              className="bg-background-50 rounded-lg border border-background-200/70 p-3 hover:border-background-300/60 transition-all"
            >
              {/* MT4 Account Info */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-6 h-6 rounded-md bg-accent-500/10 flex items-center justify-center shrink-0">
                    <i className="ri-signal-tower-line text-accent-400 text-xs"></i>
                  </div>
                  <span className="text-xs font-heading font-semibold text-foreground-900 truncate">{mt4.account_name}</span>
                </div>
                <span className={cn(
                  'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0',
                  mt4.is_live
                    ? 'bg-primary-500/10 text-primary-400 border border-primary-500/20'
                    : 'bg-secondary-500/10 text-secondary-400 border border-secondary-500/20'
                )}>
                  <span className={cn('w-1 h-1 rounded-full', mt4.is_live ? 'bg-primary-500' : 'bg-secondary-500')}></span>
                  {mt4.is_live ? 'Live' : 'Demo'}
                </span>
              </div>

              <div className="text-[10px] font-mono text-foreground-500 mb-3">
                {mt4.account_id}
                {mt4.balance !== undefined && (
                  <span> · {(mt4.currency || '$')}{(mt4.balance || 0).toLocaleString()}</span>
                )}
              </div>

              {/* Source cTrader Account selector */}
              <div className="space-y-2">
                <div>
                  <label className="text-[9px] font-medium text-foreground-500 uppercase tracking-wide block mb-1">
                    Copy from cTrader Account
                  </label>
                  <select
                    value={assignment?.ctraderAccountId || ''}
                    onChange={(e) => {
                      updateAssignment(mt4.account_id, mt4.account_name, 'ctraderAccountId', e.target.value);
                      // Reset strategy when changing source
                      updateAssignment(mt4.account_id, mt4.account_name, 'strategyId', '');
                    }}
                    className="w-full px-2.5 py-1.5 bg-background-100 border border-background-300/60 rounded-md text-[10px] text-foreground-900 focus:outline-none focus:border-accent-500/50 cursor-pointer"
                  >
                    <option value="">Select source account...</option>
                    {ctraderAccounts.map((acc) => (
                      <option key={acc.account_id} value={acc.account_id}>
                        {acc.account_name} ({acc.is_live ? 'Live' : 'Demo'})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Strategy selector — only shown when source is selected */}
                {assignment?.ctraderAccountId && (
                  <div>
                    <label className="text-[9px] font-medium text-foreground-500 uppercase tracking-wide block mb-1">
                      Strategy to Copy
                    </label>
                    <select
                      value={assignment?.strategyId || ''}
                      onChange={(e) => updateAssignment(mt4.account_id, mt4.account_name, 'strategyId', e.target.value)}
                      className="w-full px-2.5 py-1.5 bg-background-100 border border-background-300/60 rounded-md text-[10px] text-foreground-900 focus:outline-none focus:border-accent-500/50 cursor-pointer"
                    >
                      <option value="">Select strategy...</option>
                      {sourceStrategies.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name} ({s.symbol} · {s.timeframe}) {s.is_active ? '●' : '○'}
                        </option>
                      ))}
                      {sourceStrategies.length === 0 && (
                        <option disabled>No strategies for this account</option>
                      )}
                    </select>
                  </div>
                )}

                {/* Current assignment indicator */}
                {assignment?.ctraderAccountId && assignment?.strategyId && (
                  <div className="flex items-center gap-1.5 pt-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400"></div>
                    <span className="text-[9px] text-emerald-400 font-medium">
                      {getAccountName(assignment.ctraderAccountId)} → {getStrategyName(assignment.strategyId)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}